import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '../components/layout/SiteHeader';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';
import BrandLogo from '../components/brand/BrandLogo';
import { ticketsAPI } from '../lib/api';
import { getEventImageUrl } from '../utils/mediaUrl';
import {
  formatDisplayDate,
  formatTimeRange,
  getEventLocationDetail
} from '../utils/eventFormat';
import {
  generateBrandedQrDataUrl,
  QR_SIZE_DISPLAY,
  QR_SIZE_DOWNLOAD
} from '../utils/brandedQr';

const TicketPage = () => {
  const { ticketId } = useParams();
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchTicketData();
  }, [ticketId]);

  const fetchTicketData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await ticketsAPI.getTicket(ticketId);
      if (result.success) {
        setTicketData(result.data.ticket);
        generateQRCode(result.data.ticket);
      } else {
        setError(result.message || 'Ticket not found');
      }
    } catch (err) {
      setError(err.message || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const buildTicketQrPayload = (ticket) =>
    JSON.stringify({
      ticketId: ticket.id,
      eventId: ticket.eventId,
      validationCode: ticket.validationCode,
      ticketNumber: ticket.ticketNumber
    });

  const generateQRCode = async (ticket) => {
    try {
      const qrUrl = await generateBrandedQrDataUrl(buildTicketQrPayload(ticket), {
        size: QR_SIZE_DISPLAY
      });
      setQrCodeUrl(qrUrl);
    } catch (err) {
      console.error('Error generating QR code:', err);
    }
  };

  const downloadTicketPdf = async () => {
    try {
      setDownloading(true);
      setError('');
      await ticketsAPI.downloadTicketPdf(ticketId);
    } catch (err) {
      setError(err.message || 'Failed to download ticket. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const downloadQrPng = async () => {
    if (!ticketData) return;
    try {
      const hiRes = await generateBrandedQrDataUrl(buildTicketQrPayload(ticketData), {
        size: QR_SIZE_DOWNLOAD
      });
      const link = document.createElement('a');
      link.href = hiRes;
      link.download = `ticket-${ticketData.ticketNumber || ticketId}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('QR download failed:', err);
    }
  };

  const shareTicket = async () => {
    const title = `${ticketData.event.title} - Ticket`;
    const text = `My ticket for ${ticketData.event.title}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: window.location.href });
        return;
      }
    } catch {
      // fall through to clipboard
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert('Ticket link copied');
    } catch {
      alert('Could not share this ticket link');
    }
  };

  if (loading) {
    return (
      <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50/80 to-stone-100">
        <SiteHeader />
        <SiteMain className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-orange-500 border-t-transparent" />
        </SiteMain>
      </SiteLayout>
    );
  }

  if (error && !ticketData) {
    return (
      <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50/80 to-stone-100">
        <SiteHeader />
        <SiteMain className="flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Ticket not found</h2>
            <p className="text-stone-600 mb-6">{error}</p>
            <Link
              to="/events"
              className="inline-block bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors"
            >
              Browse events
            </Link>
          </div>
        </SiteMain>
      </SiteLayout>
    );
  }

  const event = ticketData.event;
  const location = getEventLocationDetail(event);
  const timeRange = formatTimeRange(event.startTime, event.endTime);
  const dateLabel = formatDisplayDate(event.date) || new Date(event.date).toLocaleDateString('en-KE');
  const packageName = ticketData.ticketType || ticketData.pricingTier?.name || 'General';

  return (
    <SiteLayout className="bg-gradient-to-br from-amber-50 via-orange-50/80 to-stone-100">
      <SiteHeader />
      <SiteMain className="py-8 lg:py-12">
        <div className="max-w-5xl mx-auto px-4">
          <div className="mb-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-2">
              Your ticket
            </p>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
              You&apos;re locked in
            </h1>
            <p className="text-stone-600 mt-2">
              Event details, QR code, and download - ready for the door.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-stone-200/80 shadow-sm overflow-hidden">
            {/* Hero strip with poster */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-4 sm:items-start sm:justify-between">
                <div className="flex gap-4 min-w-0">
                  <img
                    src={getEventImageUrl(event.imageUrl)}
                    alt={event.title}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover ring-2 ring-white/30 shrink-0 bg-orange-400"
                  />
                  <div className="min-w-0">
                    <BrandLogo link={false} size="sm" imgClassName="rounded-md ring-1 ring-white/30 mb-2" />
                    <h2 className="text-xl sm:text-2xl font-bold leading-snug">{event.title}</h2>
                    <p className="text-orange-100 text-sm mt-1">Ticket #{ticketData.ticketNumber}</p>
                  </div>
                </div>
                <span className="self-start rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
                  {packageName}
                </span>
              </div>
            </div>

            <div className="p-5 sm:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Details */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-400 mb-3">
                      Event details
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs text-stone-400 mb-0.5">Date</p>
                        <p className="font-medium text-gray-900">{dateLabel}</p>
                        {timeRange && <p className="text-sm text-stone-500">{timeRange}</p>}
                      </div>
                      <div>
                        <p className="text-xs text-stone-400 mb-0.5">Venue</p>
                        <p className="font-medium text-gray-900">{location?.label || event.venue?.name || 'TBA'}</p>
                        {(location?.sublabel || event.venue?.address || event.venue?.city) && (
                          <p className="text-sm text-stone-500">
                            {location?.sublabel || [event.venue?.address, event.venue?.city].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-stone-400 mb-0.5">Ticket holder</p>
                        <p className="font-medium text-gray-900">
                          {ticketData.buyerFirstName} {ticketData.buyerLastName}
                        </p>
                        <p className="text-sm text-stone-500">{ticketData.buyerEmail}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-stone-100 bg-stone-50/80 p-4 space-y-2.5 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-stone-500">Ticket number</span>
                      <span className="font-mono font-semibold text-gray-900">{ticketData.ticketNumber}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-stone-500">Validation code</span>
                      <span className="font-mono font-semibold text-gray-900">{ticketData.validationCode}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-stone-500">Purchased</span>
                      <span className="font-medium text-gray-900">
                        {new Date(ticketData.createdAt).toLocaleDateString('en-KE')}
                      </span>
                    </div>
                    <div className="flex justify-between gap-3 pt-2 border-t border-stone-200">
                      <span className="text-stone-500">Paid</span>
                      <span className="font-bold text-orange-600">
                        Ksh {Number(ticketData.price || 0).toLocaleString('en-KE')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* QR */}
                <div className="flex flex-col items-center lg:items-stretch">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-400 mb-3 w-full text-center lg:text-left">
                    Entry QR
                  </h3>
                  <div className="bg-white border border-stone-200 rounded-2xl p-5 flex justify-center shadow-sm">
                    {qrCodeUrl ? (
                      <img src={qrCodeUrl} alt="Ticket QR Code" className="w-52 h-52 sm:w-56 sm:h-56" />
                    ) : (
                      <div className="w-52 h-52 bg-stone-100 animate-pulse rounded-lg" />
                    )}
                  </div>
                  <p className="text-sm text-stone-500 text-center mt-3 mb-4">
                    Flash this at the door - one scan, you&apos;re in.
                  </p>

                  <div className="w-full space-y-2.5">
                    <button
                      type="button"
                      onClick={downloadTicketPdf}
                      disabled={downloading}
                      className="w-full bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 disabled:opacity-50 transition-colors"
                    >
                      {downloading ? 'Downloading…' : 'Download ticket PDF'}
                    </button>
                    <button
                      type="button"
                      onClick={downloadQrPng}
                      disabled={!qrCodeUrl}
                      className="w-full bg-white border border-stone-200 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-stone-50 disabled:opacity-50 transition-colors"
                    >
                      Download QR image
                    </button>
                    <button
                      type="button"
                      onClick={shareTicket}
                      className="w-full bg-stone-100 text-stone-700 px-6 py-3 rounded-xl font-semibold hover:bg-stone-200 transition-colors"
                    >
                      Share ticket link
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-stone-50 px-5 sm:px-8 py-5 border-t border-stone-100">
              <p className="text-sm font-semibold text-gray-900 mb-2">Quick notes</p>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• One use only - don&apos;t forward this QR.</li>
                <li>• Tickets are non-refundable once paid.</li>
                <li>• Roll up ~30 minutes early with a valid ID.</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={`/events/${event.id}`}
              className="text-center bg-white border border-stone-200 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-stone-50 transition-colors"
            >
              View event
            </Link>
            <Link
              to="/events"
              className="text-center bg-white border border-stone-200 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-stone-50 transition-colors"
            >
              Browse more events
            </Link>
            <Link
              to="/dashboard"
              className="text-center bg-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors"
            >
              My tickets
            </Link>
          </div>
        </div>
      </SiteMain>
    </SiteLayout>
  );
};

export default TicketPage;
